import { createAsyncThunk } from '@reduxjs/toolkit';
import axios from 'axios';
import { apiClientWithApi, identityApiClientWithApi } from '../../utils/http';
import {
  FormType,
  FormAnswerRestrictionsResponse,
  Guid,
  UserListResponse,
} from '../../models/TaskForm/TaskForm';
import { FORM_TYPES, FormTypesPayload, FormTypeLabels } from './TaskFormState';
import { repeatSerializer } from './TaskFormQuery';
/**
 * Fetches available form types and their localized labels from backend.
 *
 * The endpoint may return one of:
 *   - `[{ key: FormType, value: string }]`  (current contract — preferred)
 *   - `[{ value: FormType, text: string }]` (legacy serializer variant)
 *   - `[FormType, ...]`                      (string-only fallback)
 *
 * Keys are validated against the local `FormType` enum so that values used
 * for `Filter.Type` queries always match the backend enum. If the response is
 * empty/invalid we fall back to all enum keys with no labels — UI components
 * gracefully fall back to rendering the raw key.
 */
export const getTaskFormTypes = createAsyncThunk<FormTypesPayload>(
  'taskForm/getTypes',
  async () => {
    const fallback: FormTypesPayload = { types: FORM_TYPES, labels: {} };

    try {
      const response = await apiClientWithApi.get<unknown>('task/form/type');
      const data = response.data;
      if (!Array.isArray(data) || data.length === 0) return fallback;

      const types: FormType[] = [];
      const labels: FormTypeLabels = {};

      for (const item of data) {
        let key: string | undefined;
        let label: string | undefined;

        if (typeof item === 'string') {
          key = item;
        } else if (item && typeof item === 'object') {
          const obj = item as Record<string, unknown>;
          // Prefer the documented `{ key, value }` shape; fall back to legacy
          // `{ value, text }` and `{ name, ... }` variants.
          const rawKey = obj.key ?? obj.value ?? obj.name;
          const rawLabel = obj.value !== obj.key ? obj.value : undefined;
          const rawLabelFallback = obj.text ?? obj.label ?? obj.title;

          if (typeof rawKey === 'string') key = rawKey;
          if (typeof rawLabel === 'string' && rawLabel !== rawKey) {
            label = rawLabel;
          } else if (typeof rawLabelFallback === 'string') {
            label = rawLabelFallback;
          }
        }

        if (!key || !FORM_TYPES.includes(key as FormType)) continue;
        const formType = key as FormType;
        types.push(formType);
        if (label) labels[formType] = label;
      }

      return types.length > 0 ? { types, labels } : fallback;
    } catch {
      return fallback;
    }
  },
);

export const getTaskFormAnswerRestrictions = createAsyncThunk<
  FormAnswerRestrictionsResponse,
  void,
  { rejectValue: string }
>('taskForm/getAnswerRestrictions', async (_, { rejectWithValue }) => {
  try {
    const response = await apiClientWithApi.get<FormAnswerRestrictionsResponse>(
      'task/form/answer/restriction',
    );
    return response.data;
  } catch (error) {
    if (axios.isAxiosError(error)) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Не удалось получить типы ответов анкеты!');
  }
});

export const getUsersByIds = createAsyncThunk<UserListResponse, Guid[]>(
  'taskForm/getUsersByIds',
  async (userIds) => {
    const uniqueUserIds = Array.from(new Set(userIds.filter(Boolean)));
    if (!uniqueUserIds.length) return [];

    try {
      const response = await identityApiClientWithApi.get<UserListResponse>(
        'user/list',
        {
          params: { userIds: uniqueUserIds },
          paramsSerializer: repeatSerializer,
        },
      );
      return response.data;
    } catch {
      return [];
    }
  },
);
